<template>
	<NumericSettingInput v-bind="$attrs" :model-value="displayModelValue" :min-value="props.minValue"
		:max-value="props.maxValue" :step="props.step" :decimal-length="decimalLength" :suffix="displaySuffix"
		@update:model-value="handleUpdateModelValue" @blur="handleBlur" />
</template>

<script setup lang="ts">
import { computed } from "vue";
import NumericSettingInput from "./NumericSettingInput.vue";
import { useUnitDisplay } from "@/composables/useUnitDisplay";
import { distanceUnits } from "@/models/siteUnit.types";

defineOptions({ inheritAttrs: false });

/** Unit the bound value is expressed in. Metric sites also display this unit. */
export type TDistanceBaseUnit = "m" | "mm";

type Props = {
	modelValue?: number | string | null;
	minValue?: number;
	maxValue?: number;
	decimalLength?: number;
	step?: number;
	baseUnit?: TDistanceBaseUnit;
};

const props = withDefaults(defineProps<Props>(), { baseUnit: "m" });

/** Metres per base unit. SI prefix only, unrelated to the site distance unit. */
const METERS_PER_BASE_UNIT: Record<TDistanceBaseUnit, number> = {
	m: 1,
	mm: 0.001,
};

const emit = defineEmits<{
	"update:model-value": [number | undefined | null];
	blur: [];
}>();

const { distanceLabel, toDisplayDistance, fromDisplayDistance, siteUnits } =
	useUnitDisplay();

const decimalLength = computed(() => props.decimalLength ?? 3);

// Metric sites keep showing the base unit, so a mm field stays in mm there.
const isMetricSite = computed(
	() => siteUnits.value.distance === distanceUnits.METERS,
);

const displaySuffix = computed(() =>
	isMetricSite.value ? props.baseUnit : distanceLabel.value,
);

const convertToDisplayDistance = (value: number | undefined | null) => {
	if (value === undefined || value === null) {
		return value;
	}
	if (isMetricSite.value) {
		// The display unit equals the base unit, so only the decimal limit applies.
		return Number(value.toFixed(decimalLength.value));
	}
	const meters = value * METERS_PER_BASE_UNIT[props.baseUnit];
	return toDisplayDistance(meters, decimalLength.value).value;
};

const displayModelValue = computed(() => {
	if (props.modelValue === undefined || props.modelValue === null) {
		return props.modelValue;
	}
	if (typeof props.modelValue === "string") {
		const numericValue = Number(props.modelValue);
		if (Number.isNaN(numericValue)) {
			return undefined;
		}
		return convertToDisplayDistance(numericValue);
	}
	return convertToDisplayDistance(props.modelValue);
});

const handleUpdateModelValue = (value: number | undefined | null) => {
	if (value === undefined || value === null) {
		emit("update:model-value", undefined);
		return;
	}
	if (isMetricSite.value) {
		emit("update:model-value", value);
		return;
	}
	const meters = fromDisplayDistance(value);
	emit("update:model-value", meters / METERS_PER_BASE_UNIT[props.baseUnit]);
};

const handleBlur = () => {
	emit("blur");
};
</script>
